/* components/patterns/WorkspaceSwitcher.jsx — переключатель пространства (S5 — полная версия).
   Триггер: иконка типа + имя + стрелка. Под именем — строка тарифа: ярлык плана +
   расход запросов X/Y (chat_messages из /api/subscription/me/), ниже — статус подписки
   («истекает через N дн.» если ≤7). Личное (kind=personal) показываем как «Моё пространство»
   (имя ws на бэке = ФИО владельца). Dropdown: список пространств (галочка активного),
   разделитель, «+ Создать заведение» / «Управлять» → /settings?tab=business.
   Переключение проходит через UnsavedChanges.guard (confirmNavigation при грязной форме).
   Состояния: closed / open / switching(spinner) / error / single-hint (одно ws → без dropdown).
   Источник: 02-IA §4, 06 §2.8. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useUnsaved } from '../../app/providers/UnsavedChangesProvider.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Popover } from '../ui/Popover.jsx';
import { fmt } from '../../lib/format.js';
import { hooks } from '../../hooks/index.jsx';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { workspaceName as displayName } from '../../lib/workspace.js';
import { RR } from '../../lib/router.jsx';

  const { useState, useCallback, useEffect, useRef } = React;
  const { useFakeQuery } = hooks;
  const { useNavigate } = RR;

  // дней до истечения подписки (null → бессрочно)
  function daysLeft(expires_at) {
    if (!expires_at) return null;
    const d = new Date(expires_at); if (isNaN(d)) return null;
    return Math.ceil((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  }

  function WsAvatar({ ws, sm }) {
    const kind = (ws && ws.kind) || 'personal';
    // Личное («Моё пространство») — всегда логотип Ресториума, свой логотип не ставится.
    if (kind === 'personal') {
      return (
        <span className={'ws-switcher__avatar ws-switcher__avatar--personal ws-switcher__avatar--brand' + (sm ? ' ws-switcher__avatar--sm' : '')}>
          <img src="/logo-mark.svg" alt="" />
        </span>
      );
    }
    // Фото заведения из RestaurantProfile (раздел «Заведение») — аватарка
    // пространства. Нет фото → дефолтный значок по типу пространства.
    const photo = ws && (ws.photo_url || ws.photoUrl);
    return (
      <span className={'ws-switcher__avatar ws-switcher__avatar--' + kind + (sm ? ' ws-switcher__avatar--sm' : '') + (photo ? ' ws-switcher__avatar--photo' : '')}>
        {photo
          ? <img src={photo} alt="" />
          : <Icon name="restaurant" size={sm ? 14 : 16} />}
      </span>
    );
  }

  // предупреждение об истечении подписки (≤7 дн.); иначе ничего не рисуем
  function ExpiryWarning({ ws }) {
    const dl = daysLeft(ws?.subscription?.expires_at);
    if (dl == null || dl > 7) return null;
    return (
      <span className="ws-meta__warn t-caption">
        <Icon name="alert-triangle" size={12} />
        {dl <= 0 ? 'подписка истекла' : `истекает через ${dl} ${fmt.plural(dl, 'день', 'дня', 'дней')}`}
      </span>
    );
  }

  // ярлык тарифа для триггера (Free / Pro / Business)
  function planLabelOf(ws) {
    const s = ws && ws.subscription;
    if (!s) return null;
    return s.planLabel || (s.plan ? s.plan.charAt(0).toUpperCase() + s.plan.slice(1) : null);
  }

  // расход запросов из /api/subscription/me/ (chat_messages); -1 → безлимит (∞)
  function usageOf(sub) {
    if (!sub) return null;
    const used = sub.usage ? sub.usage.chat_messages_used : null;
    const lim = sub.limits ? sub.limits.chat_messages_per_30d : null;
    if (used == null || lim == null) return null;
    return { used, limit: lim < 0 ? '∞' : lim };
  }

  // строка под именем: ярлык тарифа + расход запросов X/Y
  function PlanLine({ ws, usage }) {
    const planLabel = planLabelOf(ws);
    if (!planLabel && !usage) return null;
    return (
      <span className="ws-switcher__sub t-caption text-mute">
        {planLabel && <span className="ws-switcher__plan" title={planLabel}>{planLabel}</span>}
        {planLabel && usage && <span className="ws-switcher__sep" aria-hidden="true">·</span>}
        {usage && <span className="ws-switcher__usage num">{usage.used} / {usage.limit}</span>}
      </span>
    );
  }

  // лицо переключателя: аватар + имя + строка тарифа/расхода + предупреждение об истечении
  function SwitcherFace({ ws, usage }) {
    return (
      <>
        <WsAvatar ws={ws} />
        <span className="ws-switcher__meta workspace-switcher__meta grow">
          <span className="ws-switcher__name truncate">{displayName(ws)}</span>
          <PlanLine ws={ws} usage={usage} />
          <ExpiryWarning ws={ws} />
        </span>
      </>
    );
  }

  // индикатор расхода (сообщения: потрачено / доступно)
  function UsageMeter({ usage }) {
    if (!usage) return null;
    const pct = usage.limit ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
    const tone = pct >= 90 ? 'bad' : pct >= 70 ? 'warn' : 'ok';
    return (
      <div className="usage-meter">
        <div className="usage-meter__head">
          <span className="t-caption text-dim">{usage.label}</span>
          <span className="t-caption num">{usage.used} / {usage.limit}</span>
        </div>
        <div className="usage-meter__track">
          <div className={'usage-meter__fill usage-meter__fill--' + tone} style={{ width: pct + '%' }} />
        </div>
      </div>
    );
  }

  function WorkspaceSwitcher() {
    const { workspaces, activeWorkspace, switchWorkspace } = useAuth();
    const navigate = useNavigate();
    const unsaved = (typeof useUnsaved === 'function') ? useUnsaved() : null;
    const [pending, setPending] = useState(null); // id переключаемого ws
    const [error, setError] = useState(null);

    const single = workspaces.length <= 1;

    // confirmNavigation: при грязной форме спросить, затем переключить
    const doSwitch = useCallback((ws, close) => {
      setError(null);
      const run = () => {
        setPending(ws.id);
        // имитация задержки переключения (перезагрузка контекста)
        setTimeout(() => {
          try { switchWorkspace(ws.id); setPending(null); close && close(); }
          catch (e) { setPending(null); setError('Не удалось переключить пространство'); }
        }, 240);
      };
      if (unsaved && unsaved.guard) unsaved.guard(run);
      else run();
    }, [switchWorkspace, unsaved]);

    // расход активного ws: usage нет в activeWorkspace (см. AuthProvider) — тянем из /me/,
    // перезапрос на смену пространства (ключ по id). Сбой → строка просто без X/Y.
    const aId = activeWorkspace?.id;
    const usageQ = useFakeQuery('ws-usage:' + (aId ?? 'none'), () => subscriptionReal.getSubscription(), [aId]);
    const usage = usageOf(usageQ.data);

    // Живой счётчик: расход меняется без перезагрузки страницы прямо в процессе —
    // на завершении хода (сообщение засчиталось) и на коммите (блюда меню = N
    // запросов). Источник сигнала — window-событие 'quota:changed' (ChatProvider
    // onDone + DraftEntityCard commit). reload() перезапрашивает /api/subscription/me/.
    const reloadRef = useRef(usageQ.reload);
    reloadRef.current = usageQ.reload;
    useEffect(() => {
      const h = () => { if (reloadRef.current) reloadRef.current(); };
      window.addEventListener('quota:changed', h);
      return () => window.removeEventListener('quota:changed', h);
    }, []);

    const trigger = (
      // data-through-scrim: виден под затемнением редактора «Продукции» — клик закрывает его
      // через сторожа и открывает список (lib/scrimPass.js, QA Г3 I07)
      <button className="ws-switcher" aria-label="Сменить пространство" data-onb="ws-switcher" data-through-scrim="">
        <SwitcherFace ws={activeWorkspace} usage={usage} />
        {!single && <Icon name="chevron-down" size={16} className="ws-switcher__chev" />}
      </button>
    );

    // одно пространство → без dropdown (hint)
    if (single) {
      return <div className="ws-switcher ws-switcher--single" data-onb="ws-switcher">
        <SwitcherFace ws={activeWorkspace} usage={usage} />
      </div>;
    }

    return (
      <Popover placement="bottom-start" width={300} trigger={trigger}>
        {({ close }) => (
          <div className="stack stack-2">
            <div className="popover__label">Пространства</div>
            {workspaces.map((w) => {
              const isActive = w.id === activeWorkspace?.id;
              const isPending = pending === w.id;
              return (
                <button key={w.id} role="menuitem"
                  className={'popover__item' + (isActive ? ' is-current' : '')}
                  disabled={isPending}
                  onClick={() => { if (!isActive) doSwitch(w, close); else close(); }}>
                  <WsAvatar ws={w} sm />
                  <span className="stack grow" style={{ minWidth: 0, gap: 2 }}>
                    <span className="truncate">{displayName(w)}</span>
                    <span className="t-caption text-mute truncate">{w.membership.role_name}</span>
                  </span>
                  <span className="cluster cluster-3" style={{ flexShrink: 0 }}>
                    {isPending
                      ? <Icon name="spinner" size={16} className="text-mute" />
                      : isActive ? <Icon name="check" size={16} className="text-accent" /> : null}
                  </span>
                </button>
              );
            })}
            {error && (
              <div className="ws-switcher__error t-caption" role="alert">
                <Icon name="alert-triangle" size={12} /> {error}
              </div>
            )}
            <div className="popover__sep" />
            <button className="popover__item" role="menuitem"
              onClick={() => { navigate('/settings?tab=business&create=1'); close(); }}>
              <Icon name="plus" size={16} />
              <span className="grow">Создать заведение</span>
            </button>
            <button className="popover__item" role="menuitem"
              onClick={() => { navigate('/settings?tab=business'); close(); }}>
              <Icon name="settings" size={16} />
              <span className="grow">Управлять пространствами</span>
            </button>
          </div>
        )}
      </Popover>
    );
  }

export { WorkspaceSwitcher };
export { UsageMeter };
